/**
 * CalendarDayCell component - Renders a single day cell in the calendar
 */

import React from 'react'
import { Tooltip } from 'antd'
import { useIntl } from 'react-intl'
import { MiniCalendarDay } from '../types'
import { formatTooltip } from '../utils'
import { useAppSelector } from '../../../../../../store/hooks'
import { selectLocaleSlice } from '../../../../../../store/locale-slice'

interface CalendarDayCellProps {
  day: MiniCalendarDay
}

/**
 * Gets the CSS class name for a day type
 */
function getDayStyleClass(dayType: MiniCalendarDay['type']): string {
  switch (dayType) {
    case 'pto':
      return 'mini-calendar-day-pto'
    case 'holiday':
      return 'mini-calendar-day-holiday'
    case 'weekend':
      return 'mini-calendar-day-weekend'
    case 'workday':
      return 'mini-calendar-day-workday'
    default:
      return 'mini-calendar-day-empty'
  }
}

/**
 * CalendarDayCell renders a single day cell with appropriate styling and tooltip
 */
export const CalendarDayCell: React.FC<CalendarDayCellProps> = ({ day }) => {
  const { formatMessage } = useIntl()
  const { locale } = useAppSelector(selectLocaleSlice)

  if (day.type === 'empty') {
    return <div className="mini-calendar-day mini-calendar-day-empty" />
  }

  const styleClass = getDayStyleClass(day.type)
  const tooltipText = formatTooltip(day, formatMessage, locale.locale)

  return (
    <Tooltip title={tooltipText} placement="top">
      <div className={`mini-calendar-day ${styleClass}`}>{day.date.getDate()}</div>
    </Tooltip>
  )
}
